import { useMemo } from 'react';
import { PlaidTransaction } from '../types/transactions';
import { isZelle } from '../utils/payment-type';
import { canonicalizeKeys } from '../utils/merchant-normalizer';
import { getVendorDisplayLabel } from '../utils/vendor-label';

export type XactoGroupBy = 'category' | 'merchant' | 'vendor' | 'subcategory';

export interface XactoRow {
  month: string;
  values: Record<string, number>;
  rowTotal: number;
}

export interface XactoCheckEntry {
  /** Total amount of checks received (deposits) — always positive */
  in: number;
  /** Total amount of checks issued (payments) — always positive */
  out: number;
}

export interface XactoMatrixData {
  /** Dynamic group labels (categories or merchants), sorted */
  columns: string[];
  /** Maps each column key to its human-readable display name */
  columnLabels: Record<string, string>;
  /** One row per month, sorted ascending */
  rows: XactoRow[];
  /** colTotals[col] = sum across all months; colTotals.grandTotal = overall sum */
  colTotals: Record<string, number>;
  /** Check amounts per month + grand totals. Only populated when checkNumber is present. */
  checkData: {
    rows: Record<string, XactoCheckEntry>;
    totals: XactoCheckEntry;
  };
  /** Zelle amounts per month + grand totals. Only populated when transaction is Zelle. */
  zelleData: {
    rows: Record<string, XactoCheckEntry>;
    totals: XactoCheckEntry;
  };
  /**
   * Only populated for category groupBy.
   * Maps primary category name → sorted list of composite column keys ("primary::detailed").
   */
  categoryGroups?: Record<string, string[]>;
}

/**
 * Transforms flat PlaidTransaction[] into an inverted pivot:
 *   Rows    = Months (YYYY-MM)
 *   Columns = Categories OR Merchants
 *   Values  = sum of amount (Plaid convention: positive = expense)
 *
 * Missing month/group combinations are represented as 0 (displayed as "-").
 */
export const useXactoMatrix = (
  data: PlaidTransaction[],
  groupBy: XactoGroupBy,
  period: 'month' | 'year' = 'month',
): XactoMatrixData => {
  return useMemo(() => {
    const emptyCheckData = { rows: {}, totals: { in: 0, out: 0 } };

    if (!data.length) {
      return { columns: [], columnLabels: {}, rows: [], colTotals: {}, checkData: emptyCheckData, zelleData: emptyCheckData };
    }

    // Pass 1: accumulate sums into monthGroupMap[month][group]
    const monthGroupMap: Record<string, Record<string, number>> = {};
    const allGroups = new Set<string>();
    const groupLabels: Record<string, string> = {}; // key → display name
    const checkAmounts: Record<string, XactoCheckEntry> = {};
    const zelleAmounts: Record<string, XactoCheckEntry> = {};
    // Only used for category groupBy: tracks primary → set of composite keys
    const primaryCategoryMap: Record<string, Set<string>> = {};

    for (const t of data) {
      const month = t.date.substring(0, period === 'year' ? 4 : 7);
      const amount = t.amount || 0;

      let group: string;
      if (groupBy === 'merchant') {
        group = t.merchantName || t.name || 'Unknown';
        groupLabels[group] = group;
      } else if (groupBy === 'vendor') {
        group = getVendorDisplayLabel(t);
        groupLabels[group] = group;
      } else if (groupBy === 'subcategory') {
        group = t.personalFinanceCategoryDetailed || t.personalFinanceCategoryPrimary || 'Uncategorized';
        groupLabels[group] = group;
      } else {
        const primary = t.personalFinanceCategoryPrimary || 'Uncategorized';
        const detailed = t.personalFinanceCategoryDetailed || 'General';
        group = `${primary}::${detailed}`;
        groupLabels[group] = detailed;
        if (!primaryCategoryMap[primary]) primaryCategoryMap[primary] = new Set();
        primaryCategoryMap[primary].add(group);
      }

      allGroups.add(group);

      // Accounting convention (GAAP / cash flow):
      //   income (cash in)  → positive (+)
      //   expense (cash out) → negative (-)
      // Plaid is inverted (amount>0 = debit/expense), so we negate.
      const accountingAmount = -amount;

      if (!monthGroupMap[month]) monthGroupMap[month] = {};
      monthGroupMap[month][group] = (monthGroupMap[month][group] || 0) + accountingAmount;

      // Accumulate check amounts separately (stored as always-positive)
      if (t.checkNumber) {
        if (!checkAmounts[month]) checkAmounts[month] = { in: 0, out: 0 };
        if (amount < 0) {
          // Plaid credit (amount < 0) = money coming in = check deposit
          checkAmounts[month].in += Math.abs(amount);
        } else if (amount > 0) {
          // Plaid debit (amount > 0) = money going out = check payment
          checkAmounts[month].out += amount;
        }
      }

      // Accumulate Zelle amounts separately (stored as always-positive)
      if (isZelle(t)) {
        if (!zelleAmounts[month]) zelleAmounts[month] = { in: 0, out: 0 };
        if (amount < 0) {
          zelleAmounts[month].in += Math.abs(amount);
        } else if (amount > 0) {
          zelleAmounts[month].out += amount;
        }
      }
    }

    // Pass 2: canonicalize merchant keys (only for merchant groupBy)
    // Merges groups that represent the same merchant via proximity analysis.
    if (groupBy === 'merchant') {
      const canonical = canonicalizeKeys(allGroups);
      const hasRemap = Array.from(canonical.entries()).some(([k, v]) => k !== v);
      if (hasRemap) {
        // Rebuild monthGroupMap and allGroups with canonical keys
        for (const month of Object.keys(monthGroupMap)) {
          const monthData = monthGroupMap[month];
          const remapped: Record<string, number> = {};
          for (const [group, amount] of Object.entries(monthData)) {
            const canonKey = canonical.get(group) ?? group;
            remapped[canonKey] = (remapped[canonKey] || 0) + amount;
          }
          monthGroupMap[month] = remapped;
        }
        // Remap labels: for each old key, assign its label to the canonical key
        for (const [oldKey, canonKey] of canonical.entries()) {
          if (oldKey !== canonKey && !groupLabels[canonKey]) {
            groupLabels[canonKey] = groupLabels[oldKey] || canonKey;
          }
        }
        allGroups.clear();
        for (const month of Object.keys(monthGroupMap)) {
          for (const group of Object.keys(monthGroupMap[month])) {
            allGroups.add(group);
          }
        }
      }
    }

    // Pass 3: compute group totals (used for sorting and colTotals)
    const groupTotals: Record<string, number> = {};
    for (const group of allGroups) {
      groupTotals[group] = Object.values(monthGroupMap).reduce(
        (sum, monthData) => sum + (monthData[group] || 0),
        0,
      );
    }

    // Sort columns: INCOME groups (net positive total) first alphabetically, then EXPENSE groups alphabetically
    let columns: string[];
    let categoryGroups: Record<string, string[]> | undefined;

    if (groupBy === 'category') {
      const primaryTotals: Record<string, number> = {};
      for (const [primary, keys] of Object.entries(primaryCategoryMap)) {
        primaryTotals[primary] = Array.from(keys).reduce((s, k) => s + (groupTotals[k] || 0), 0);
      }
      const sortedPrimaries = Object.keys(primaryCategoryMap).sort((a, b) => {
        const aIncome = (primaryTotals[a] ?? 0) > 0;
        const bIncome = (primaryTotals[b] ?? 0) > 0;
        if (aIncome !== bIncome) return aIncome ? -1 : 1;
        return a.localeCompare(b);
      });
      categoryGroups = {};
      columns = [];
      for (const primary of sortedPrimaries) {
        const subkeys = Array.from(primaryCategoryMap[primary]).sort((a, b) => {
          const aIncome = (groupTotals[a] ?? 0) > 0;
          const bIncome = (groupTotals[b] ?? 0) > 0;
          if (aIncome !== bIncome) return aIncome ? -1 : 1;
          return (groupLabels[a] ?? a).localeCompare(groupLabels[b] ?? b);
        });
        categoryGroups[primary] = subkeys;
        columns.push(...subkeys);
      }
    } else {
      columns = Array.from(allGroups).sort((a, b) => {
        const aIncome = (groupTotals[a] ?? 0) > 0;
        const bIncome = (groupTotals[b] ?? 0) > 0;
        if (aIncome !== bIncome) return aIncome ? -1 : 1;
        return a.localeCompare(b);
      });
    }

    // Build rows (one per month, sorted ascending)
    const sortedMonths = Object.keys(monthGroupMap).sort();

    const rows: XactoRow[] = sortedMonths.map(month => {
      const monthData = monthGroupMap[month];
      const rowTotal = columns.reduce((sum, col) => sum + (monthData[col] || 0), 0);
      return { month, values: monthData, rowTotal };
    });

    // Column totals
    const colTotals: Record<string, number> = {};
    for (const col of columns) {
      colTotals[col] = groupTotals[col];
    }
    colTotals.grandTotal = rows.reduce((sum, r) => sum + r.rowTotal, 0);

    // Check grand totals
    const checkTotals: XactoCheckEntry = { in: 0, out: 0 };
    for (const entry of Object.values(checkAmounts)) {
      checkTotals.in += entry.in;
      checkTotals.out += entry.out;
    }

    // Zelle grand totals
    const zelleTotals: XactoCheckEntry = { in: 0, out: 0 };
    for (const entry of Object.values(zelleAmounts)) {
      zelleTotals.in += entry.in;
      zelleTotals.out += entry.out;
    }

    return {
      columns,
      columnLabels: groupLabels,
      rows,
      colTotals,
      checkData: { rows: checkAmounts, totals: checkTotals },
      zelleData: { rows: zelleAmounts, totals: zelleTotals },
      categoryGroups,
    };
  }, [data, groupBy, period]);
};
